/**
 * Release 1 F1 — the board filter bar ("filter spine"). One popover per predicate group (Type, Approval, Tags,
 * Colour, Date, People, Fields, More), active-filter chips with remove buttons, result count and clear-all.
 * State is owned by the page (URL); this component only maps clicks to the next `BoardFilters`.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ChevronDown, X } from 'lucide-react';
import type { ApprovalStatus, AssetType, Facets } from '@/lib/api/types';
import { useFields, useMembers, usePeople, useTags } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { useGuestMode } from '@/features/board-guests/useGuestMode'; // release3/F
import { Input } from '@/components/Input';
import { cx, plural } from '@/lib/format';
import { useFocusTrap } from '@/lib/useFocusTrap';
import { Buckets, ColorSwatches, CustomFieldRows, DatePresetButtons, DateRangeFields, PeopleChips, TagMatchSelect, type Bucket } from '@/features/search/predicates/Predicates';
import { approvalLabel, countBoardFilters, filterChips, toggleValue, typeLabel, EMPTY_BOARD_FILTERS, type BoardFilters } from '@/features/search/predicates/serialize';
import { SaveBoardFiltersAsSmart } from '@/features/smart-boards/SaveAsSmartBoard'; // release2/A

export type FilterBarProps = {
  filters: BoardFilters;
  onChange: (f: BoardFilters) => void;
  facets: Facets | undefined;
  /** total of the filtered listing (first page), null while unknown */
  total: number | null;
  loading?: boolean;
  /** right-aligned extras (view switcher, display menu) */
  children?: ReactNode;
  /** Release 2 S3: offer "Save as smart board" next to "Clear all" (on by default; off inside the smart board dialog) */
  saveAsSmart?: boolean;
};

const TYPES: AssetType[] = ['image', 'video', 'audio', 'document', 'other'];
const APPROVALS: ApprovalStatus[] = ['needs_review', 'in_progress', 'approved', 'none'];
const ORIENTATIONS = ['landscape', 'portrait', 'square'];

/** Facet buckets merged with the full option list so a zero-count option is still selectable. */
function withAll(buckets: Bucket[] | undefined, all: readonly string[], labelOf: (v: string) => string): Bucket[] {
  const counts = new Map((buckets ?? []).map((b) => [b.value, b.count]));
  return all.map((value) => ({ value, label: labelOf(value), count: counts.get(value) ?? 0 }));
}

export function FilterBar({ filters, onChange, facets, total, loading, children, saveAsSmart = true }: FilterBarProps) {
  const wsId = useSessionStore((s) => s.workspace?.id);
  const tags = useTags(wsId);
  const fields = useFields(wsId);
  const people = usePeople(wsId);
  const guestMode = useGuestMode(); // release3/F: the member directory is 403 for guests
  const members = useMembers(guestMode ? undefined : wsId);
  const active = countBoardFilters(filters);
  const chips = useMemo(() => filterChips(filters, { tags: tags.data, fields: fields.data, people: people.data, members: members.data }), [filters, tags.data, fields.data, people.data, members.data]);
  const n = (key: keyof BoardFilters) => {
    const v = filters[key];
    return Array.isArray(v) ? v.length : v ? 1 : 0;
  };
  const [tagQuery, setTagQuery] = useState('');
  const tagBuckets = useMemo<Bucket[]>(() => {
    const q = tagQuery.trim().toLowerCase();
    if (q) return (tags.data ?? []).filter((t) => t.name.toLowerCase().includes(q)).slice(0, 12).map((t) => ({ value: t.id, label: t.name, count: facets?.tags?.find((b) => b.value === t.id)?.count ?? 0 }));
    const fromFacets = (facets?.tags ?? []).map((b) => ({ ...b, label: b.label ?? tags.data?.find((t) => t.id === b.value)?.name ?? b.value }));
    return fromFacets.length ? fromFacets : (tags.data ?? []).slice(0, 12).map((t) => ({ value: t.id, label: t.name, count: 0 }));
  }, [tagQuery, tags.data, facets?.tags]);
  const peopleFallback = useMemo<Bucket[]>(() => (people.data ?? []).filter((p) => !p.hidden).slice(0, 20).map((p) => ({ value: p.id, label: p.name ?? 'Unnamed', count: p.asset_count })), [people.data]);
  const memberBuckets = useMemo<Bucket[]>(() => (members.data ?? []).map((m) => ({ value: m.id, label: m.user.display_name ?? m.user.email ?? m.id, count: facets?.uploaded_by?.find((b) => b.value === m.id)?.count ?? 0 })), [members.data, facets?.uploaded_by]);

  return (
    <div className={cx('mb-3 flex flex-col gap-2', loading && 'opacity-80')} data-filter-bar aria-busy={loading || undefined}>
      <div className="flex flex-wrap items-center gap-1.5" role="toolbar" aria-label="Filters">
        <FilterPopover label="Type" count={n('types')}>
          <Buckets buckets={withAll(facets?.types, TYPES, typeLabel)} selected={filters.types ?? []} onToggle={(v) => onChange(toggleValue(filters, 'types', v))} />
        </FilterPopover>
        <FilterPopover label="Approval" count={n('approval')}>
          <Buckets buckets={withAll(facets?.approval, APPROVALS, approvalLabel)} selected={filters.approval ?? []} onToggle={(v) => onChange(toggleValue(filters, 'approval', v))} />
        </FilterPopover>
        <FilterPopover label="Tags" count={n('tag_ids')} wide>
          <Input aria-label="Find tag" placeholder="Find tag…" value={tagQuery} onChange={(e) => setTagQuery(e.target.value)} className="mb-1 h-7 text-xs" />
          <Buckets buckets={tagBuckets} selected={filters.tag_ids ?? []} onToggle={(v) => onChange(toggleValue(filters, 'tag_ids', v))} limit={12} />
          <TagMatchSelect filters={filters} onChange={onChange} />
        </FilterPopover>
        <FilterPopover label="Colour" count={n('colors')} wide>
          <ColorSwatches facets={facets?.colors} filters={filters} onChange={onChange} />
        </FilterPopover>
        <FilterPopover label="Date" count={n('date')} wide>
          <div className="space-y-2">
            <DatePresetButtons filters={filters} onChange={onChange} />
            <DateRangeFields filters={filters} onChange={onChange} fields={fields.data ?? []} />
          </div>
        </FilterPopover>
        <FilterPopover label="People" count={n('people')} wide>
          <PeopleChips buckets={facets?.people} fallback={peopleFallback} selected={filters.people ?? []} onToggle={(v) => onChange(toggleValue(filters, 'people', v))} />
        </FilterPopover>
        <FilterPopover label="Fields" count={n('custom_fields')} wide>
          <CustomFieldRows fields={fields.data ?? []} filters={filters} onChange={onChange} facets={facets?.custom_fields} />
        </FilterPopover>
        <FilterPopover label="More" count={n('orientation') + n('uploaded_by') + n('has_transcript')} wide>
          <div className="space-y-2 text-xs">
            <div>
              <div className="mb-0.5 font-semibold text-fg-subtle uppercase">Orientation</div>
              <Buckets buckets={withAll(facets?.orientation, ORIENTATIONS, (v) => v[0]!.toUpperCase() + v.slice(1))} selected={filters.orientation ?? []} onToggle={(v) => onChange(toggleValue(filters, 'orientation', v))} />
            </div>
            <div>
              <div className="mb-0.5 font-semibold text-fg-subtle uppercase">Uploaded by</div>
              <Buckets buckets={memberBuckets} selected={filters.uploaded_by ?? []} onToggle={(v) => onChange(toggleValue(filters, 'uploaded_by', v))} />
            </div>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!filters.has_transcript} onChange={(e) => onChange({ ...filters, has_transcript: e.target.checked || undefined })} /> has transcript</label>
          </div>
        </FilterPopover>
        <span className="ml-auto flex items-center gap-2 text-sm text-fg-subtle">
          {total != null && <span aria-live="polite" data-result-count>{active ? `${plural(total, 'asset', 'assets')} match` : plural(total, 'asset', 'assets')}</span>}
          {children}
        </span>
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-xs" data-filter-chips aria-label="Active filters">
          {chips.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent-soft px-2 py-0.5 text-accent" data-chip={c.group}>
              {c.label}
              <button type="button" aria-label={`Remove filter ${c.label}`} onClick={() => onChange(c.remove(filters))} className="rounded hover:text-danger focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"><X size={11} /></button>
            </span>
          ))}
          <button type="button" className="ml-1 text-accent hover:underline" onClick={() => onChange({ ...EMPTY_BOARD_FILTERS })} data-clear-filters>Clear all</button>
          {saveAsSmart && <SaveBoardFiltersAsSmart filters={filters} />}
        </div>
      )}
    </div>
  );
}

/** Button + anchored dialog; Escape / outside click close it and focus returns to the button. */
function FilterPopover({ label, count, children, wide }: { label: string; count: number; children: ReactNode; wide?: boolean }) {
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  useFocusTrap(panel, { active: open, onEscape: () => setOpen(false), initialFocus: 'first' });
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!panel.current?.contains(t) && !btn.current?.contains(t)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown, true);
    return () => document.removeEventListener('pointerdown', onDown, true);
  }, [open]);
  return (
    <div className="relative">
      <button ref={btn} type="button" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen((o) => !o)} className={cx('inline-flex h-7 items-center gap-1 rounded-full border px-2.5 text-xs font-medium transition-colors', count ? 'border-accent bg-accent-soft text-accent' : 'border-line hover:bg-bg-sunken')} data-filter-group={label.toLowerCase()}>
        {label}
        {count > 0 && <span className="rounded-full bg-accent px-1.5 text-[10px] leading-4 text-accent-fg">{count}</span>}
        <ChevronDown size={12} className="opacity-70" />
      </button>
      {open && (
        <div ref={panel} role="dialog" aria-label={`${label} filters`} tabIndex={-1} className={cx('absolute top-full left-0 z-30 mt-1 rounded-md border border-line bg-bg-elev p-2 text-sm shadow-lg', wide ? 'w-80' : 'w-56')}>
          {children}
        </div>
      )}
    </div>
  );
}
